'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { buildPublicUrl } from '@/lib/r2-urls';
import safeURL from '@/lib/url';

const MAX_IMAGES = 10;
const DEFAULT_INTERVAL = 6000;
const TRANSITION_MS = 1200;
const TRANSITION_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

const usePrefersReducedMotion = () => {
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia)
            return undefined;

        const mediaQuery = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        );
        const updatePreference = (event) => {
            setPrefersReducedMotion(event.matches);
        };

        setPrefersReducedMotion(mediaQuery.matches);

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', updatePreference);
            return () =>
                mediaQuery.removeEventListener('change', updatePreference);
        }

        mediaQuery.addListener(updatePreference);
        return () => mediaQuery.removeListener(updatePreference);
    }, []);

    return prefersReducedMotion;
};

const normalizeImages = (rawImages = []) => {
    if (!Array.isArray(rawImages)) return [];
    return rawImages
        .filter((img) => img && (img.src || img.filename))
        .slice(0, MAX_IMAGES)
        .map((img, index) => {
            const baseSrc = img.src || `/images/${img.filename}`;
            // Resolve to the public URL (CDN) so client and server use identical URLs
            let resolvedBase;
            try {
                resolvedBase = buildPublicUrl(baseSrc) || baseSrc;
            } catch (_e) {
                resolvedBase = baseSrc;
            }

            const normalizedSources = Array.isArray(img.sources)
                ? img.sources
                      .filter((source) => source?.src && source.width)
                      .map((source) => {
                          let resolved;
                          try {
                              resolved =
                                  buildPublicUrl(source.src) || source.src;
                          } catch (_e) {
                              resolved = source.src;
                          }
                          return {
                              ...source,
                              src: safeURL(resolved),
                          };
                      })
                : undefined;

            return {
                key: `${img.filename || resolvedBase || 'image'}-${index}`,
                src: safeURL(resolvedBase),
                alt: img.alt || `Slideshow image ${index + 1}`,
                blurDataURL: img.blurDataURL,
                sources: normalizedSources,
                fetchPriority: img.fetchPriority,
            };
        });
};

const Slideshow = ({
    images = [],
    interval = DEFAULT_INTERVAL,
    className = '',
    onClientReady,
    showOverlay = true,
    skipFirstImage = false,
}) => {
    const normalizedImages = useMemo(() => normalizeImages(images), [images]);
    // When skipFirstImage is true, the first image is server-rendered in Hero.js
    // so we start from index 0 (which becomes slide 2 visually) after hydration.
    // Start with no active slide to avoid immediately replacing the server-rendered
    // LCP image. We'll activate the first slide after transitions are enabled.
    const [index, setIndex] = useState(normalizedImages.length > 0 ? -1 : 0);
    const timerRef = useRef(null);
    const prefersReducedMotion = usePrefersReducedMotion();
    const [hydrated, setHydrated] = useState(false);
    // enableTransition flips true on the next animation frame after hydration
    // This ensures the browser has applied initial styles so opacity changes
    // between slides animate rather than jump instantly on first transition.
    const [enableTransition, setEnableTransition] = useState(false);
    const readyNotifiedRef = useRef(false);
    const firstSlideLoadedRef = useRef(false);

    const notifyReady = useCallback(() => {
        if (readyNotifiedRef.current) return;
        readyNotifiedRef.current = true;
        if (typeof onClientReady === 'function') {
            try {
                onClientReady();
            } catch (_e) {
                // ignore notification errors
            }
        }
    }, [onClientReady]);

    // Reset index when the image list changes
    useEffect(() => {
        setIndex(normalizedImages.length > 0 ? -1 : 0);
        readyNotifiedRef.current = false;
        firstSlideLoadedRef.current = false;
    }, [normalizedImages]);

    // Mark hydrated on client so we can enable transitions after mount
    useEffect(() => {
        setHydrated(true);
    }, []);

    // After hydration, enable transitions on the next RAF tick to allow
    // initial styles to settle. This helps the first change animate.
    useEffect(() => {
        if (!hydrated) return undefined;
        if (normalizedImages.length === 0) return undefined;

        const activate = () => {
            setEnableTransition(true);
            setIndex((prev) => (prev === -1 ? 0 : prev));
        };

        let cancel = () => {};
        let scheduled = false;

        if (typeof window !== 'undefined') {
            try {
                const rafId = window.requestAnimationFrame(activate);
                scheduled = true;
                cancel = () => {
                    try {
                        window.cancelAnimationFrame?.(rafId);
                    } catch (_err) {
                        // ignore cancel errors
                    }
                };
            } catch (_err) {
                // ignore and fall back to setTimeout
            }

            if (!scheduled) {
                try {
                    const timeoutId = window.setTimeout(activate, 16);
                    scheduled = true;
                    cancel = () => {
                        try {
                            window.clearTimeout(timeoutId);
                        } catch (_err) {
                            // ignore cancel errors
                        }
                    };
                } catch (_err) {
                    // ignore; run immediately below
                }
            }
        }

        if (!scheduled) {
            activate();
        }

        return () => {
            try {
                cancel();
            } catch (_err) {
                // ignore cancel errors
            }
        };
    }, [hydrated, normalizedImages.length]);

    // Lightweight interval loop
    useEffect(() => {
        if (normalizedImages.length <= 1) return undefined;
        const tick = () => {
            setIndex((prev) => (prev + 1) % normalizedImages.length);
        };
        const effectiveInterval = Math.max(
            interval,
            prefersReducedMotion ? 8000 : 2000,
        );
        timerRef.current = window.setInterval(tick, effectiveInterval);
        return () => {
            if (timerRef.current) {
                window.clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [normalizedImages.length, interval, prefersReducedMotion]);

    useEffect(() => {
        if (!enableTransition) return;
        if (index < 0) return;
        if (!firstSlideLoadedRef.current) return;
        notifyReady();
    }, [enableTransition, index, notifyReady]);

    // Prefetch the next image using the browser cache
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        if (normalizedImages.length <= 1) return undefined;

        const nextImage =
            normalizedImages[(index + 1) % normalizedImages.length];
        if (!nextImage?.src) return undefined;

        // Resolve the prefetch URL to the same public URL AppImage will request
        let prefetchUrl;
        try {
            prefetchUrl = buildPublicUrl(nextImage.src);
        } catch (_e) {
            prefetchUrl = nextImage.src;
        }

        const preload = new window.Image();
        try {
            preload.decoding = 'async';
        } catch (_e) {}

        let timedOut = false;
        const TIMEOUT_MS = 8000;
        const timeoutId = window.setTimeout(() => {
            timedOut = true;
            try {
                preload.src = '';
            } catch (_e) {}
            if (process.env && process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn(
                    '[slideshow] prefetch next image timed out:',
                    prefetchUrl,
                );
            }
        }, TIMEOUT_MS);

        const onload = () => {
            if (!timedOut) {
                try {
                    window.clearTimeout(timeoutId);
                } catch (_e) {}
            }
        };
        const onerror = (err) => {
            try {
                window.clearTimeout(timeoutId);
            } catch (_e) {}
            if (process.env && process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn(
                    '[slideshow] prefetch next image error:',
                    prefetchUrl,
                    err,
                );
            }
        };

        preload.addEventListener('load', onload);
        preload.addEventListener('error', onerror);
        try {
            preload.src = prefetchUrl;
        } catch (e) {
            onerror(e);
        }

        return () => {
            try {
                preload.removeEventListener('load', onload);
                preload.removeEventListener('error', onerror);
            } catch (_e) {}
            try {
                preload.src = '';
            } catch (_e) {}
            try {
                window.clearTimeout(timeoutId);
            } catch (_e) {}
        };
    }, [index, normalizedImages]);

    if (normalizedImages.length === 0) {
        return (
            <div
                className={`absolute inset-0 overflow-hidden ${className}`}
                aria-hidden
            >
                <div className="absolute inset-0 bg-linear-to-br from-brand to-brand-light" />
                <div className="absolute inset-0 bg-black/45" />
            </div>
        );
    }

    // When skipFirstImage is true, the first image is already server-rendered
    // in Hero.js, so we skip rendering it here to avoid duplicate DOM/requests
    const imagesToRender = skipFirstImage
        ? normalizedImages.slice(1)
        : normalizedImages;

    // Adjust the active index when skipping first image
    const _adjustedIndex = skipFirstImage ? index - 1 : index;

    return (
        <div
            className={`absolute inset-0 overflow-hidden ${className}`}
            aria-hidden
        >
            {imagesToRender.map((image, imageIndex) => {
                // When skipFirstImage, imageIndex 0 in imagesToRender is actually slide 2
                const originalIndex = skipFirstImage
                    ? imageIndex + 1
                    : imageIndex;
                // When skipFirstImage, index 0 means first slideshow image (which is server-rendered)
                // so the slideshow should show nothing until index >= 1
                const isInitialActive =
                    !skipFirstImage && index < 0 && imageIndex === 0;
                const isActive = originalIndex === index || isInitialActive;
                const handleLoad =
                    originalIndex === (skipFirstImage ? 1 : 0)
                        ? () => {
                              firstSlideLoadedRef.current = true;
                              if (enableTransition && index >= 0) {
                                  notifyReady();
                              }
                          }
                        : undefined;

                // When skipFirstImage, none of the slideshow images are priority
                // since the LCP image is server-rendered in Hero.js
                const isPriorityImage = !skipFirstImage && originalIndex === 0;
                // Boost fetch priority for images 1-3 (slides 2-4) even when lazy-loaded
                const shouldHighFetchPriority =
                    !skipFirstImage && originalIndex >= 1 && originalIndex <= 3;

                return (
                    <div
                        key={image.key}
                        className="absolute inset-0"
                        style={{
                            opacity: isActive ? 1 : 0,
                            pointerEvents: isActive ? 'auto' : 'none',
                            transition: prefersReducedMotion
                                ? undefined
                                : enableTransition && !isInitialActive
                                  ? `opacity ${TRANSITION_MS}ms ${TRANSITION_EASING}`
                                  : 'none',
                            // Only set will-change for the currently active slide to avoid
                            // allocating large will-change memory across many elements.
                            // Browsers ignore excessive will-change usage and may warn if
                            // the budget is exceeded. Restricting to the active slide
                            // keeps memory usage low while still hinting for the one
                            // element that's actually transitioning.
                            willChange:
                                prefersReducedMotion || !enableTransition
                                    ? undefined
                                    : isActive
                                      ? 'opacity'
                                      : undefined,
                        }}
                    >
                        <AppImage
                            src={image.src}
                            alt={image.alt}
                            fill
                            priority={isPriorityImage}
                            priorityForLCP={isPriorityImage}
                            fetchPriority={
                                image.fetchPriority ||
                                (shouldHighFetchPriority || isPriorityImage
                                    ? 'high'
                                    : undefined)
                            }
                            className="object-cover"
                            placeholder={image.blurDataURL ? 'blur' : 'empty'}
                            blurDataURL={image.blurDataURL}
                            sizes="100vw"
                            // Only aggressively preload the first image — reduce preloads for others
                            // When skipFirstImage, first slideshow image is already server-rendered
                            enablePreload={isPriorityImage}
                            preloadRootMargin={
                                isPriorityImage
                                    ? undefined
                                    : '200px 0px 200px 0px'
                            }
                            onLoad={handleLoad}
                        />
                        {/* Per-slide overlay ensures each slide has the same dark treatment */}
                        <div
                            className="absolute inset-0 bg-black/45 pointer-events-none"
                            style={{
                                opacity: showOverlay ? 1 : 0,
                                transition: `opacity ${Math.min(TRANSITION_MS, 600)}ms ${TRANSITION_EASING}`,
                            }}
                        />
                    </div>
                );
            })}
            {/* No global overlay; overlays are rendered per slide above. */}
        </div>
    );
};

export default Slideshow;
